import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { SearchClientService } from "@app2/clients/search-client.service";
import { DEFAULT_CUSTOM_COLUMNS } from "@app2/search/search-shared/constants";
import { NotificationsService } from "@app2/shared/services/notifications.service";

@Component({
    selector: "customize-search-columns",
    template: `
        <i *ngIf="loading" class="fa fa-spinner fa-spin"></i>
        <form *ngIf="!loading"
              class="nice-form">
            <label>
                <autocomplete-input [placeholder]="'TYPE_FIELD_NAME' | localize"
                                    [allOptions]="validFields"
                                    [excludedOptions]="excludedColumns"
                                    (optionSelected)="addColumn($event)">
                </autocomplete-input>
                <div class="input-caption">{{ 'SEARCH_ADD_COLUMNS_EXPLANATION' | localize }}</div>
            </label>
            <p *ngIf="errorMessageKey" class="form-error-text" test-id="error-message">
                {{ errorMessageKey | localize }}
            </p>

            <div class="search-columns">
                <h3 class="sub-header">{{ "CUSTOM_COLUMNS" | localize }}</h3>
                <div class="custom-col-item declutter disabled-text">
                    {{ "TIMESTAMP" | localize }}
                </div>
                <div class="drag-n-drop-list"
                     cdkDropList (cdkDropListDropped)="onCustomColumnsReordered($event)">
                    <div cdkDrag
                         *ngFor="let column of customColumns; let index = index"
                         class="custom-col-item declutter drag-cursor"
                         [attr.test-id]="'custom-column-' + index">
                        <span>{{ column }}</span>
                        <button *ngIf="customColumns?.length > 1"
                                class="link alert delete-button show-on-hover"
                                type="button"
                                (click)="removeColumn(column)"
                                [attr.test-id]="'remove-column-' + index">
                            <i [matTooltip]="'SEARCH_DELETE_CUSTOM_COLUMN_TOOLTIP' | localize"
                               class="far fa-times alert"></i>
                        </button>
                    </div>
                </div>
            </div>
        </form>
    `,
    styles: [`
        @import "/src/styles/colors";

        .sub-header {
            margin: 24px 0;
        }

        .drag-n-drop-list {
            display: flex;
            flex-direction: column;
        }

        .custom-col-item {
            display: flex;
            justify-content: space-between;
            padding: 0.125rem 0;

            &:hover {
            }

            .delete-button {
                padding: 2px 4px 0 4px;
            }
        }
    `,
    ],
})
export class CustomizeSearchColumnsComponent implements OnInit, OnChanges {
    @Input() customColumns: string[];
    @Input() maxColumns: number;
    @Input() defaultEmpty: boolean;
    @Input() allOrgs: boolean; // Will only succeed if it's a PrAdmin
    @Input() includeNested: boolean;

    @Output() columnsUpdated = new EventEmitter<string[]>();

    loading: boolean;
    validFields: string[] = [];
    excludedColumns: string[] = [];
    errorMessageKey: string;

    constructor(private searchClient: SearchClientService,
                private notificationsService: NotificationsService) {
    }

    ngOnInit() {
        this.loading = true;
        const promise = this.allOrgs
            ? this.searchClient.getAllFieldsForAllOrgs(this.includeNested)
            : this.searchClient.getAllFields(this.includeNested);
        promise
            .then(fields => this.validFields = fields)
            .catch(error => this.notificationsService.showError("ERROR_LOADING_FIELDS", error))
            .finally(() => this.loading = false);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.customColumns) {
            if (this.customColumns.length === 0) {
                this.customColumns = this.defaultEmpty ? [] : DEFAULT_CUSTOM_COLUMNS;
            }
            this.excludedColumns = ["timestamp", ...this.customColumns];
        }
    }

    addColumn(newColumn: string) {
        newColumn = newColumn.trim();
        if (newColumn.length === 0) {
            this.errorMessageKey = "SEARCH_ERR_EMPTY_COLUMN";
            return;
        }
        // Don't add duplicate columns
        const isExcluded = this.excludedColumns.some((existingColumn) => existingColumn === newColumn);
        if (isExcluded) {
            this.errorMessageKey = "SEARCH_ERR_DUPLICATE_COLUMN";
            return;
        }
        // Heuristic to try to find invalid column names
        if (!/^\w+(\.\w+)*$/.test(newColumn)) {
            this.errorMessageKey = "SEARCH_ERR_NON_EXISTENT_COLUMN";
            return;
        }
        if (this.maxColumns && (this.customColumns.length + 1 === this.maxColumns)) {
            this.errorMessageKey = "SEARCH_ERR_MAX_COLUMNS";
            return;
        }

        this.customColumns = [...this.customColumns, newColumn];
        this.excludedColumns = [...this.excludedColumns, newColumn];
        this.errorMessageKey = null;
        this.columnsUpdated.emit(this.customColumns);
    }

    removeColumn(customColumn) {
        this.customColumns = this.customColumns.filter(column => customColumn !== column);
        this.excludedColumns = this.excludedColumns.filter(column => customColumn !== column);
        this.columnsUpdated.emit(this.customColumns);
    }

    onCustomColumnsReordered(event: CdkDragDrop<string[]>) {
        const customColumns = [...this.customColumns];
        customColumns.splice(event.previousIndex, 1); // Remove old
        customColumns.splice(event.currentIndex, 0, this.customColumns[event.previousIndex]); // Insert in new position

        this.customColumns = customColumns;
        this.columnsUpdated.emit(this.customColumns);
    }
}
